import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';

const BrandingContext = createContext(null);

export const useBranding = () => useContext(BrandingContext) || {};

const DEFAULTS = {
  primary_color: '#0052FF',
  secondary_color: '#0F172A',
  accent_color: '#22C55E',
  email_primary_color: '#0052FF',
  font_family: 'Inter',
  app_name: 'PayrollPro',
  logo_url: 'https://hecs.in/storage/logo/hecs-logo-1.png',
  favicon_url: '',
  layout_density: 'comfortable',
};

const setMetaIcon = (href) => {
  if (!href) return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
};

export const BrandingProvider = ({ children }) => {
  const { user } = useAuth();
  // Branding lives on the user object (returned by /api/auth/me). Fall back to defaults.
  const branding = useMemo(
    () => ({ ...DEFAULTS, ...(user?.branding || {}) }),
    [user?.branding]
  );

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', branding.primary_color);
    root.style.setProperty('--brand-secondary', branding.secondary_color);
    root.style.setProperty('--brand-accent', branding.accent_color);
    root.style.setProperty('--brand-font', `"${branding.font_family}", Inter, system-ui, sans-serif`);

    // Apply font-family at body level (Tailwind classes still win where set)
    document.body.style.fontFamily = `var(--brand-font)`;

    // Layout density → body class for any global tweaks
    document.body.classList.remove('density-comfortable', 'density-compact');
    document.body.classList.add(`density-${branding.layout_density || 'comfortable'}`);

    // Favicon
    if (branding.favicon_url) setMetaIcon(branding.favicon_url);
    else if (branding.logo_url) setMetaIcon(branding.logo_url);

    // App name → window title (so browser tabs reflect it)
    if (branding.app_name && document.title.indexOf(branding.app_name) === -1) {
      document.title = `${branding.app_name} — Payroll Suite`;
    }
  }, [branding]);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingProvider;
